import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useState, useEffect, useRef } from 'react'
import { formatInTimeZone } from 'date-fns-tz'
import { motion, AnimatePresence } from 'framer-motion'
import {
  PencilSquareIcon,
  CalendarDaysIcon,
  MapPinIcon,
  CameraIcon,
  ShieldCheckIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  GlobeAmericasIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import FormField from '../common/FormField'
import FormSection from '../common/FormSection'
import EventMap from '../common/EventMap'
import LoadingSpinner from '../common/LoadingSpinner'
import ErrorMessage from '../common/ErrorMessage'
import Turnstile from '../common/Turnstile'

const eventSchema = z.object({
  name: z.string().min(3, 'Name must be at least 3 characters long'),
  description: z.string().min(10, 'Description must be at least 10 characters long'),
  start_date: z.string().min(1, 'Start date is required'),
  end_date: z.string().min(1, 'End date is required'),
  location: z.object({
    address: z.string().min(1, 'Address is required'),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    zip_code: z.string().min(1, 'Zip code is required'),
    latitude: z.string().min(1, 'Latitude is required'),
    longitude: z.string().min(1, 'Longitude is required'),
    timezone: z.string().optional()
  }),
  radius: z.coerce.number().min(0.1, 'Radius must be at least 0.1 miles').max(50, 'Radius must be less than 50 miles'),
  requires_approval: z.boolean().optional(),
  photo: z.union([
    z.instanceof(File).refine(
      (file) => file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024,
      'Photo must be an image under 5MB'
    ),
    z.null()
  ]).optional(),
  remove_photo: z.boolean().optional()
}).refine(
  (data) => {
    const [startYear, startMonth, startDay] = data.start_date.split('-').map(Number)
    const [endYear, endMonth, endDay] = data.end_date.split('-').map(Number)
    const startOfStartDay = new Date(startYear, startMonth - 1, startDay)
    const startOfEndDay = new Date(endYear, endMonth - 1, endDay)
    return startOfEndDay >= startOfStartDay
  },
  {
    message: "End date must be after or the same as start date",
    path: ["end_date"]
  }
)

export default function EventForm({
  initialData = null,
  onSubmit,
  loading,
  error,
  submitLabel = 'Submit Event',
  onCancel
}) {
  const [photoPreview, setPhotoPreview] = useState(null)
  const [hasExistingPhoto, setHasExistingPhoto] = useState(!!initialData?.photo_url)
  const [turnstileToken, setTurnstileToken] = useState(null)
  const [turnstileError, setTurnstileError] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef(null)

  const eventTimezone = initialData?.timezone || 'UTC'
  const formatDateForInput = (dateString) => {
    return formatInTimeZone(new Date(dateString), eventTimezone, 'yyyy-MM-dd')
  }

  const formattedInitialData = initialData ? {
    ...initialData,
    start_date: formatDateForInput(initialData.start_date),
    end_date: formatDateForInput(initialData.end_date),
    radius: parseFloat(initialData.radius)
  } : null

  const form = useForm({
    resolver: zodResolver(eventSchema),
    defaultValues: formattedInitialData || {
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      location: {
        address: '',
        city: '',
        state: '',
        zip_code: '',
        latitude: '',
        longitude: ''
      },
      radius: 2,
      requires_approval: false
    },
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const { setValue, watch, formState: { errors } } = form
  const startDate = watch('start_date')

  useEffect(() => {
    const endDate = watch('end_date')
    if (startDate && endDate) {
      const start = new Date(startDate)
      const end = new Date(endDate)
      if (end < start) {
        setValue('end_date', startDate, { shouldValidate: true })
      }
    }
  }, [startDate, setValue, watch])

  useEffect(() => {
    if (initialData) {
      const locationData = initialData.location ? {
        address: initialData.location.address || '',
        city: initialData.location.city || '',
        state: initialData.location.state || '',
        zip_code: initialData.location.zip_code || '',
        latitude: initialData.location.latitude?.toString() || '',
        longitude: initialData.location.longitude?.toString() || ''
      } : {
        address: '',
        city: '',
        state: '',
        zip_code: '',
        latitude: '',
        longitude: ''
      }

      form.reset({
        ...initialData,
        start_date: formatDateForInput(initialData.start_date),
        end_date: formatDateForInput(initialData.end_date),
        radius: parseFloat(initialData.radius),
        location: locationData
      })
    }
  }, [initialData, form])

  const handleSubmit = async (data) => {
    if (!initialData && !turnstileToken) {
      setTurnstileError('Please complete the security verification')
      return
    }

    const submitData = {
      ...data,
      start_date: data.start_date,
      end_date: data.end_date
    }

    if (!initialData && turnstileToken) {
      submitData.turnstile_token = turnstileToken
    }

    if (!submitData.photo) delete submitData.photo
    if (!submitData.remove_photo) delete submitData.remove_photo

    try {
      await onSubmit(submitData)
    } catch (err) {
      if (!initialData) {
        setTurnstileToken(null)
        setTurnstileError(null)
      }
      throw err
    }
  }

  const handleTurnstileVerify = (token) => {
    setTurnstileToken(token)
    setTurnstileError(null)
  }

  const handleTurnstileError = () => {
    setTurnstileToken(null)
    setTurnstileError('Security verification failed. Please try again.')
  }

  const handleTurnstileExpire = () => {
    setTurnstileToken(null)
    setTurnstileError('Security verification expired. Please verify again.')
  }

  const handleLocationSelect = (location) => {
    setValue('location', location, { shouldValidate: true })
  }

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const previewUrl = URL.createObjectURL(file)
    setPhotoPreview(previewUrl)
    setHasExistingPhoto(false)
    setValue('photo', file, { shouldValidate: true })
  }

  const handlePhotoDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (!file || !file.type.startsWith('image/')) return
    const previewUrl = URL.createObjectURL(file)
    setPhotoPreview(previewUrl)
    setHasExistingPhoto(false)
    setValue('photo', file, { shouldValidate: true })
  }

  const handleRemovePhoto = () => {
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview)
      setPhotoPreview(null)
    }
    setValue('photo', null, { shouldValidate: true })
    setHasExistingPhoto(false)
  }

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview)
    }
  }, [photoPreview])

  const hasPhoto = photoPreview || hasExistingPhoto
  const isSubmitDisabled = loading || (!initialData && !turnstileToken)

  return (
    <div>
      {/* Error Banner */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="mb-6"
          >
            <div className="alert-danger">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Something went wrong</p>
                <p className="text-sm mt-0.5 opacity-90 whitespace-pre-line">{error.message}</p>
                {error.fieldErrors && (
                  <div className="mt-2 text-sm space-y-1">
                    {Object.entries(error.fieldErrors).map(([field, messages]) => (
                      <p key={field}>
                        <span className="font-medium">{field}:</span> {messages.join(', ')}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <FormProvider {...form}>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit(handleSubmit)(e)
          }}
          className="space-y-0"
        >
          {/* Main Form Card */}
          <div className="card divide-y divide-border">
            {/* Section 1: Event Details */}
            <div className="p-5 sm:p-8">
              <FormSection
                title="Event Details"
                description="Name and describe your event"
                stepNumber={1}
              >
                <FormField
                  label="Event Name"
                  id="name"
                  name="name"
                  required
                  placeholder="e.g., Neighborhood Garage Sale Weekend"
                />

                <FormField
                  label="Description"
                  id="description"
                  name="description"
                  type="textarea"
                  rows={4}
                  required
                  placeholder="Describe the event — how many households, what kinds of items, etc."
                />
              </FormSection>
            </div>

            {/* Section 2: Event Photo */}
            <div className="p-5 sm:p-8">
              <FormSection
                title="Event Photo"
                description="Add a photo to make your event stand out"
                stepNumber={2}
              >
                {hasPhoto ? (
                  <div className="relative group rounded-xl overflow-hidden border border-border
                    max-w-sm shadow-xs hover:shadow-md transition-shadow duration-200">
                    <img
                      src={photoPreview || initialData?.photo_url}
                      alt="Event photo preview"
                      className="w-full aspect-[4/3] object-cover"
                    />
                    {/* Gradient overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent
                      opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="absolute top-2 right-2 p-2 rounded-full
                        bg-red-500/90 text-white shadow-md backdrop-blur-sm
                        sm:opacity-0 sm:group-hover:opacity-100
                        hover:bg-red-600 active:scale-90
                        transition-all duration-200
                        min-h-[36px] min-w-[36px] flex items-center justify-center"
                      aria-label="Remove photo"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragEnter={(e) => { e.preventDefault(); setIsDragging(true) }}
                    onDragLeave={(e) => { e.preventDefault(); setIsDragging(false) }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handlePhotoDrop}
                    className={`cursor-pointer rounded-xl border-2 border-dashed
                      transition-all duration-250 group
                      ${isDragging
                        ? 'border-primary-500 bg-primary-50/60 dark:bg-primary-50/20 scale-[1.01] shadow-sm'
                        : 'border-surface-300 dark:border-surface-300 hover:border-primary-400 dark:hover:border-primary-400 hover:bg-primary-50/30 dark:hover:bg-primary-50/10'
                      }`}
                  >
                    <div className="flex flex-col items-center justify-center py-8 px-4">
                      <div className={`rounded-full p-3 mb-3 transition-all duration-250
                        ${isDragging
                          ? 'bg-primary-100 dark:bg-primary-100 scale-110'
                          : 'bg-surface-100 dark:bg-surface-200 group-hover:bg-primary-50 dark:group-hover:bg-primary-100'
                        }`}>
                        <ArrowUpTrayIcon className={`h-6 w-6 transition-colors duration-200
                          ${isDragging
                            ? 'text-primary-600 dark:text-primary-500'
                            : 'text-text-muted group-hover:text-primary-500'
                          }`} />
                      </div>
                      <p className="text-sm font-medium text-text mb-0.5">Upload event photo</p>
                      <p className="text-xs text-text-muted">Drag & drop or tap to browse</p>
                      <p className="text-xs text-text-muted/70 mt-0.5">JPG, PNG, WebP up to 5MB</p>
                    </div>
                    {isDragging && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="absolute inset-0 rounded-xl bg-primary-500/5 dark:bg-primary-500/10 pointer-events-none"
                      />
                    )}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="sr-only"
                />
              </FormSection>
            </div>

            {/* Section 3: Dates */}
            <div className="p-5 sm:p-8">
              <FormSection
                title="Event Dates"
                description="When does the event take place?"
                stepNumber={3}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    label="Start Date"
                    id="start_date"
                    name="start_date"
                    type="date"
                    required
                  />
                  <FormField
                    label="End Date"
                    id="end_date"
                    name="end_date"
                    type="date"
                    required
                  />
                </div>
                {/* Date range hint */}
                {startDate && (
                  <p className="text-xs text-text-muted -mt-1">
                    Tip: Most garage sale events run 1-3 days
                  </p>
                )}
              </FormSection>
            </div>

            {/* Section 4: Location & Radius */}
            <div className="p-5 sm:p-8">
              <FormSection
                title="Location & Area"
                description="Set the center location and coverage area"
                stepNumber={4}
              >
                <div className="rounded-xl border border-border shadow-xs">
                  <EventMap
                    eventLocation={initialData?.location}
                    radius={form.watch('radius')}
                    onLocationSelect={handleLocationSelect}
                    shouldFitBounds={false}
                  />
                </div>

                <FormField
                  label="Coverage Radius"
                  id="radius"
                  name="radius"
                  type="number"
                  icon={GlobeAmericasIcon}
                  suffix="miles"
                  step="0.01"
                  min="0.1"
                  max="50"
                  required
                  helpText="How far from the center should sales be allowed? (0.1 – 50 miles)"
                />
              </FormSection>
            </div>

            {/* Section 5: Approval Settings */}
            <div className="p-5 sm:p-8">
              <FormSection
                title="Sale Approval"
                description="Control how sales are added to your event"
                stepNumber={5}
              >
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    {...form.register('requires_approval')}
                    className="mt-0.5 h-5 w-5 rounded border-border text-primary-600
                      focus:ring-primary-500 focus:ring-offset-0 cursor-pointer"
                  />
                  <div>
                    <span className="text-sm font-medium text-text group-hover:text-primary-600 transition-colors">
                      Require approval for new sales
                    </span>
                    <p className="text-xs text-text-muted mt-0.5">
                      When enabled, sales submitted to this event must be approved by you before they appear publicly.
                    </p>
                  </div>
                </label>
              </FormSection>
            </div>

            {/* Section 6: Security (create only) */}
            {!initialData && (
              <div className="p-5 sm:p-8">
                <FormSection
                  title="Verification"
                  description="Quick security check before submitting"
                  stepNumber={6}
                >
                  <Turnstile
                    onVerify={handleTurnstileVerify}
                    onError={handleTurnstileError}
                    onExpire={handleTurnstileExpire}
                  />
                  <AnimatePresence>
                    {turnstileError && (
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 font-medium"
                      >
                        <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                        {turnstileError}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </FormSection>
              </div>
            )}
          </div>

          {/* Submit Area — sticky on mobile for easy thumb access */}
          <div className="sticky bottom-0 z-sticky mt-6 -mx-4 sm:mx-0 px-4 sm:px-0 py-4 sm:py-0
            bg-bg/95 backdrop-blur-sm sm:bg-transparent sm:backdrop-blur-none
            border-t border-border sm:border-0
            sm:static">
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              {onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={isSubmitDisabled}
                className={`btn-primary shadow-md hover:shadow-lg transition-shadow
                  ${!initialData ? 'w-full sm:w-auto' : ''}`}
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    {submitLabel}...
                  </>
                ) : (
                  submitLabel
                )}
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
